import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Tag } from '@vtex/shoreline'

import { Radio } from '~/components/unform'
import { formatMoney } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  getInvestmentReferenceService,
  getInvestmentReferenceServiceKey
} from '~/modules/retailMedia/services/configuration/offsite-investment-reference'

// Global mirror of CampaignChannel (declared in constants/campaignChannel.ts)
// as a plain string-literal union, not the enum -- matches how
// useCampaignForm() exposes `selectedChannel` (hooks/types.d.ts), whose
// callers are meant to consume the value structurally rather than via the
// nominal enum type.
type OffsiteChannel = CampaignChannelValue

type InvestmentObjectiveProps = {
  channel: OffsiteChannel
  onChange?: (objective: InvestmentObjectiveType) => void
  // A persistent catalog failure (Publya 503, contracts/
  // publya-investment-integration.md) DOES block advancing the investment
  // step (FR-030d), same as a simulation failure -- distinct from the
  // normal "no coverage" empty response (FR-031), which never errors.
  onReferenceError?: (hasError: boolean) => void
}

const OBJECTIVES: InvestmentObjectiveType[] = ['traffic', 'conversion']

// One query per objective, in parallel, so both cards show their reference
// parameters before the operator picks either -- not fetched only after
// selection.
const useReferenceQuery = (
  channel: OffsiteChannel,
  objective: InvestmentObjectiveType
) =>
  useQuery({
    queryKey: [getInvestmentReferenceServiceKey, channel, objective],
    queryFn: async () => {
      const response = await getInvestmentReferenceService({
        channel,
        objective
      })
      return response.data
    },
    enabled: !!channel,
    // The app-wide QueryClient (app.tsx) has no defaultOptions, so this
    // would otherwise inherit staleTime: 0 + refetchOnWindowFocus: true --
    // refetching this reference (and Projections' estimate, same fix
    // there) on every tab-focus, hitting Publya for no input change
    // (review comment, 2026-09-09). staleTime: Infinity is safe here: a
    // changed channel/objective is already a different queryKey, so this
    // only stops a same-key refetch, not a real update. (retry policy
    // deliberately left at the QueryClient default -- that's the
    // BFF/domain's call, not this layer's, per the same review thread.)
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })

const InvestmentObjective: React.FC<InvestmentObjectiveProps> = ({
  channel,
  onChange,
  onReferenceError
}) => {
  const { t } = useAPPTranslation()

  const referenceQueries: Record<
    InvestmentObjectiveType,
    ReturnType<typeof useReferenceQuery>
  > = {
    traffic: useReferenceQuery(channel, 'traffic'),
    conversion: useReferenceQuery(channel, 'conversion')
  }

  // Either objective's catalog call can fail independently (both are
  // fetched in parallel, before either is picked) -- any failure blocks
  // the step the same way (FR-030d). Checked via `error` (not `isError`),
  // matching Projections.tsx's own convention for the same kind of Publya
  // failure, in the same feature.
  const hasReferenceError =
    !!referenceQueries.traffic.error || !!referenceQueries.conversion.error

  useEffect(() => {
    onReferenceError?.(hasReferenceError)
    // onReferenceError isn't guaranteed stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReferenceError])

  // Parameters shown on each card come live from the Publya API at render
  // time (FR-030, FR-030b) -- never hardcoded screen text or local config.
  // Rendered as pills (prototype's `dv360-strategy-tag`), one per value,
  // not a single joined string -- matches the reference screenshot
  // (2026-09-01), which also expects the purchase metric itself ("Buy:
  // CPC") as the first pill, missing until now.
  const describeReference = (objective: InvestmentObjectiveType) => {
    const { data, isLoading, error } = referenceQueries[objective]

    if (isLoading) {
      return t('rm:campaign.create.InvestmentObjective.reference.loading')
    }

    // A real catalog failure (Publya 503) is distinct from the normal "no
    // coverage" empty response below -- it blocks the step (FR-030d),
    // handled by hasReferenceError/onReferenceError above.
    if (error) {
      return t('rm:campaign.create.InvestmentObjective.reference.error')
    }

    // No coverage for this channel + objective combination (FR-031) --
    // the card still exists, just without a reference to show. Publya's real
    // API returns 0 (not null/omitted) for a metric that doesn't apply to a
    // platform's purchase metric -- confirmed 2026-09-04 testing against the
    // live integration -- so 0 is treated the same as "not defined" here,
    // same as every check below.
    if (!data || (!data.cpm && !data.cpc)) {
      return t('rm:campaign.create.InvestmentObjective.reference.unavailable')
    }

    // Only fields the Publya response actually defines (non-zero) are shown.
    const tags = [
      data.purchaseMetric !== undefined && [
        'buyLabel',
        t('rm:campaign.create.InvestmentObjective.reference.buyLabel', {
          value: data.purchaseMetric
        })
      ],
      !!data.cpm && [
        'cpm',
        t('rm:campaign.create.InvestmentObjective.reference.cpm', {
          value: formatMoney(data.cpm)
        })
      ],
      !!data.cpc && [
        'cpc',
        t('rm:campaign.create.InvestmentObjective.reference.cpc', {
          value: formatMoney(data.cpc)
        })
      ],
      !!data.ctr && [
        'ctr',
        t('rm:campaign.create.InvestmentObjective.reference.ctr', {
          value: `${(data.ctr * 100).toFixed(2)}%`
        })
      ],
      !!data.frequency && [
        'frequency',
        t('rm:campaign.create.InvestmentObjective.reference.frequency', {
          value: data.frequency
        })
      ]
    ].filter(Boolean) as [string, string][]

    return (
      <div className="d-flex flex-wrap gap-2">
        {tags.map(([key, label]) => (
          <Tag key={key} variant="secondary" color="gray">
            {label}
          </Tag>
        ))}
      </div>
    )
  }

  // Each card leads with what the objective does, then the live Publya
  // parameters — the cards used to show the pills alone, leaving the
  // operator to infer traffic vs. conversion from CPM/CPC (2026-09-15 demo,
  // SPEC 004 item 6.1). Copy from the prototype (apps/offsite/v2.html,
  // adtech.*.traffic.desc / adtech.*.conversion.desc).
  const options = OBJECTIVES.map(objective => ({
    value: objective,
    label: t(`rm:campaign.create.InvestmentObjective.${objective}.label`),
    description: (
      <>
        <div>
          {t(`rm:campaign.create.InvestmentObjective.${objective}.description`)}
        </div>
        <div className="mt-2">{describeReference(objective)}</div>
      </>
    )
  }))

  return (
    <div className="mb-3">
      <strong className="d-block mb-1">
        {t('rm:campaign.create.InvestmentObjective.title')}
      </strong>
      <p className="text-muted small mb-2">
        {t('rm:campaign.create.InvestmentObjective.description')}
      </p>
      <Radio
        name="investmentObjectiveType"
        options={options}
        onChange={event => {
          const value = event.target.value as InvestmentObjectiveType
          onChange?.(value)
        }}
      />
    </div>
  )
}

export default InvestmentObjective
